import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError } from "../api/client";
import { AsyncBlock } from "../components/AsyncBlock";
import { AutoFetchPanel } from "../components/AutoFetchPanel";
import { DayCameras } from "../components/DayCameras";
import { FootageRootPanel } from "../components/FootageRootPanel";
import { NvrPanel } from "../components/NvrPanel";
import { Panel } from "../components/Panel";
import { ProgressBar } from "../components/ProgressBar";
import { Select } from "../components/Select";
import { StatusPill } from "../components/StatusPill";
import { Switch } from "../components/Switch";
import { WorkerRuntimePanel } from "../components/WorkerRuntimePanel";
import { WorkersPanel } from "../components/WorkersPanel";
import { useFilters } from "../lib/filters";
import { formatBytes, formatCount, formatDateTime, formatDuration, yesterday } from "../lib/format";
import { runtimeSummary } from "../lib/runtime";
import { urlParam, useUrlState } from "../lib/urlState";
import { useAsync } from "../lib/useAsync";
import { ui } from "../lib/ui";
import { isConfigured } from "../types";
import type { Download, Run } from "../types";

const REFRESH_MS = 5000;
const DOWNLOAD_REFRESH_MS = 1500;

const downloadActive = (d: Download) => d.status === "queued" || d.status === "running";

/** Operator console: pick a day, download it from the store's NVR and/or
 *  queue its run, and watch the workers get through the queue. */
export function Runs() {
  const { branches, defaultBranch, single } = useFilters();
  // Only branches with an entrance camera can be processed. On a branch PC
  // there is one branch, this store's, and the pickers are gone.
  const configured = branches.filter(isConfigured);
  const governorates = [...new Set(configured.map((b) => b.governorate))].sort();
  const [governorate, setGovernorate] = useState(() =>
    defaultBranch ? defaultBranch.governorate : urlParam("gov") ?? "",
  );
  const [branchId, setBranchId] = useState(() =>
    defaultBranch ? defaultBranch.id : urlParam("branch") ?? "",
  );
  const thisBranch = defaultBranch ? branches.find((b) => b.id === defaultBranch.id) : undefined;
  const canQueue = single ? !!thisBranch && isConfigured(thisBranch) : !!branchId;
  const inGovernorate = configured.filter((b) => b.governorate === governorate);
  const [runDate, setRunDate] = useState(() => urlParam("date") || yesterday());
  // Detector for the next queued run: "" = the worker machine's default.
  const [model, setModel] = useState(() => urlParam("model") ?? "");
  // Debug: write annotated QA videos (off unless asked, `debug=1`).
  // Demographics: the face pass (on unless `demo=0`).
  const [debug, setDebug] = useState(() => urlParam("debug") === "1");
  const [faces, setFaces] = useState(() => urlParam("demo") !== "0");

  useUrlState(
    {
      gov: single ? "" : governorate,
      branch: single ? "" : branchId,
      date: runDate,
      model,
      debug: debug ? "1" : "",
      demo: faces ? "" : "0",
    },
    (p) => {
      if (!single && "gov" in p) setGovernorate(p.gov);
      if (!single && "branch" in p) setBranchId(p.branch);
      if ("date" in p && p.date) setRunDate(p.date);
      if ("model" in p) setModel(p.model);
      if ("debug" in p) setDebug(p.debug === "1");
      if ("demo" in p) setFaces(p.demo !== "0");
    },
  );
  const detectors = useAsync(() => api.detectorModels(), []);
  const detectorLabel = (id: string | null) => {
    const catalogue = detectors.data;
    if (!id) {
      const fallback = catalogue?.options.find((o) => o.is_default);
      return fallback ? `default (${fallback.label})` : "default";
    }
    return catalogue?.options.find((o) => o.id === id)?.label ?? id;
  };
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyMessage, setHistoryMessage] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);
  // Bumped whenever footage on disk or its folders change so every view re-fetches.
  const [footageVersion, setFootageVersion] = useState(0);
  const footageChanged = () => setFootageVersion((v) => v + 1);

  const runs = useAsync(() => api.listRuns({ limit: 100 }), []);
  // "Send to hub" exists only where there is a hub to send to: a branch PC
  // whose store holds a hub token (a local store's counts stay here).
  const version = useAsync(() => api.version(), []);
  const hubStore =
    version.data?.mode === "branch" &&
    !!version.data.hub?.url &&
    !!version.data.branch &&
    !version.data.branch.local;
  const [syncingId, setSyncingId] = useState<number | null>(null);

  // Default to the first governorate once the list arrives, and keep the
  // branch inside the chosen governorate. A deep-linked branch pins its own
  // governorate first, and nothing is corrected before the list loads —
  // that would wipe URL-seeded values.
  const seededBranch = useRef(branchId);
  useEffect(() => {
    if (single || branches.length === 0) return;
    if (seededBranch.current) {
      const seeded = configured.find((b) => b.id === seededBranch.current);
      seededBranch.current = "";
      if (seeded) {
        setGovernorate(seeded.governorate);
        return;
      }
    }
    if (!governorate) {
      if (governorates.length > 0) setGovernorate(governorates[0]);
      return;
    }
    if (inGovernorate.length === 0) {
      if (branchId) setBranchId("");
    } else if (!inGovernorate.some((b) => b.id === branchId)) {
      setBranchId(inGovernorate[0].id);
    }
  }, [single, branches, configured, governorates, inGovernorate, governorate, branchId]);

  // The selected day's footage, the store's recorder and its downloads.
  const nvr = useAsync(
    () => (branchId ? api.nvr(branchId) : Promise.resolve(null)),
    [branchId],
  );
  const footage = useAsync(
    () => (branchId && runDate ? api.footageDay(branchId, runDate) : Promise.resolve(null)),
    [branchId, runDate, footageVersion],
  );
  const downloads = useAsync(
    () => (branchId ? api.listDownloads(branchId) : Promise.resolve([] as Download[])),
    [branchId],
  );
  const dayDownload = downloads.data?.find((d) => d.day === runDate) ?? null;
  const otherActive = downloads.data?.find((d) => downloadActive(d) && d.day !== runDate);
  const anyDownloading = (downloads.data ?? []).some(downloadActive);
  // The automatic fetch starting or finishing a download: show it here too.
  const reloadRuns = runs.reload;
  const reloadDownloads = downloads.reload;
  const autoFetchActivity = useCallback(() => {
    reloadDownloads();
    reloadRuns();
  }, [reloadDownloads, reloadRuns]);

  // Poll while anything is in flight so status changes show up on their own.
  const pending = (runs.data ?? []).some(
    (run) => run.status === "queued" || run.status === "running",
  );
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(runs.reload, REFRESH_MS);
    return () => clearInterval(timer);
  }, [pending, runs.reload]);
  useEffect(() => {
    if (!anyDownloading) return;
    const timer = setInterval(downloads.reload, DOWNLOAD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [anyDownloading, downloads.reload]);
  // A download that just finished changed what is on disk (and may have
  // queued a run): refresh both.
  const activeIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    const now = new Set((downloads.data ?? []).filter(downloadActive).map((d) => d.id));
    const finished = [...activeIds.current].some((id) => !now.has(id));
    activeIds.current = now;
    if (finished) {
      footageChanged();
      runs.reload();
    }
  }, [downloads.data, runs.reload]);

  const channels = Object.keys(nvr.data?.channels ?? {}).length;
  const canDownload =
    !!branchId && !!nvr.data?.configured && nvr.data.powershell && channels > 0;
  const downloadHint = !branchId
    ? null
    : !nvr.data?.configured
      ? "To download from the NVR, set up the recorder below."
      : channels === 0
        ? "Enter each camera's NVR channel to download it."
        : null;
  const busyDay = !!dayDownload && downloadActive(dayDownload);

  const say = (ok: string | null, bad: string | null = null) => {
    setMessage(ok);
    setError(bad);
  };
  const failed = (err: unknown) =>
    say(null, err instanceof ApiError ? err.message : String(err));

  const queue = async () => {
    say(null);
    try {
      const { run, created } = await api.createRun(branchId, runDate, "console", model, {
        annotate: debug,
        faces,
      });
      say(
        created
          ? `Run #${run.id} queued for ${run.run_date} with ${detectorLabel(run.yolo_model)}` +
            `${run.annotate ? ", annotated videos" : ""}` +
            `${run.faces === false ? ", demographics off" : ""}.`
          : `${run.run_date} is already ${run.status} as run #${run.id} — not queued again.`,
      );
      runs.reload();
    } catch (err) {
      failed(err);
    }
  };

  const download = async (thenQueue: boolean) => {
    say(null);
    try {
      const { download: job, created } = await api.startDownload(
        branchId,
        runDate,
        thenQueue,
        model,
        { annotate: debug, faces },
      );
      // The progress bar below says what is happening; only a repeat click
      // needs words.
      if (!created) say(`${job.day} is already downloading.`);
      downloads.reload();
    } catch (err) {
      failed(err);
    }
  };

  const cancelDownload = async (id: number) => {
    try {
      await api.cancelDownload(id);
      downloads.reload();
    } catch (err) {
      failed(err);
    }
  };

  const cancel = async (id: number) => {
    setHistoryError(null);
    setHistoryMessage(null);
    try {
      await api.cancelRun(id);
      runs.reload();
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : String(err));
    }
  };

  // Runs only ever add to a day's counts, so clearance is its own explicit
  // action: wipe the branch/day's stats (footfall, heatmap, staff matches,
  // track records) so the next run recounts from zero.
  const resetStats = async (branch: string, day: string, inHistory: boolean) => {
    const setOk = inHistory ? setHistoryMessage : setMessage;
    const setBad = inHistory ? setHistoryError : setError;
    const confirmed = window.confirm(
      `Reset all stats of ${branch} on ${day}?\n\n` +
        "Footfall counts, heatmap occupancy, staff match totals and the " +
        "day's track records are deleted, along with counts its recordings " +
        "left on the next day when that day has no run of its own. Uploaded " +
        "sales stay. Queue a run afterwards to recount from the footage.",
    );
    if (!confirmed) return;
    setBad(null);
    setOk(null);
    try {
      const reset = await api.resetDay(branch, day);
      setOk(
        `Reset ${branch} on ${day}: ${reset.footfall_hours} footfall hour(s), ` +
          `${reset.camera_hours} camera hour(s), ${reset.tracks} track(s), ` +
          `${reset.staff_matches} staff match row(s) removed.` +
          (reset.spill_cleared.length
            ? ` Also cleared what its recordings left on ${reset.spill_cleared.join(", ")}.`
            : "") +
          (reset.spill_kept.length
            ? ` ${reset.spill_kept.join(", ")} still has some of its counts, merged with that day's own run — reset it too for a clean recount.`
            : ""),
      );
      runs.reload();
    } catch (err) {
      setBad(err instanceof ApiError ? err.message : String(err));
    }
  };

  const reprocess = async (source: Run) => {
    setHistoryError(null);
    setHistoryMessage(null);
    setReprocessingId(source.id);
    try {
      // Same detector as the original run, so "again" means the same thing.
      const { run, created } = await api.createRun(
        source.branch_id,
        source.run_date,
        "console-reprocess",
        source.yolo_model,
        // null on rows from before the switches: the machine defaults.
        { annotate: source.annotate, faces: source.faces },
      );
      setHistoryMessage(
        created
          ? `Run #${run.id} queued for ${run.branch_id} on ${run.run_date} with ` +
            `${detectorLabel(run.yolo_model)} — its results will be added to the ` +
            "day's existing counts."
          : `${run.branch_id} on ${run.run_date} is already ${run.status} ` +
            `as run #${run.id} — not queued again.`,
      );
      runs.reload();
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setReprocessingId(null);
    }
  };

  // The agent uploads a finished day within a few minutes; this is the same
  // upload done now, for an operator who wants the hub current at once.
  const sendToHub = async (run: Run) => {
    setHistoryError(null);
    setHistoryMessage(null);
    setSyncingId(run.id);
    try {
      const sent = await api.syncRun(run.id);
      const others = sent.days.filter((d) => d !== sent.run_date);
      setHistoryMessage(
        `Sent ${sent.branch_id} on ${sent.run_date} to the hub: ${sent.footfall_hours} ` +
          `footfall hour(s), ${sent.camera_hours} camera hour(s)` +
          (others.length
            ? ` — plus ${others.join(", ")}, still waiting to be synced.`
            : "."),
      );
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSyncingId(null);
    }
  };

  const downloadNote = (job: Download): string => {
    const done = job.progress === null ? "searching the NVR…" : `${Math.round(job.progress * 100)}%`;
    const freed = job.freed_days.length
      ? ` · freed ${formatBytes(job.freed_bytes)} (${job.freed_days.length} old processed day${job.freed_days.length === 1 ? "" : "s"} deleted)`
      : "";
    return `Downloading ${job.day} — ${done}${freed}${job.then_queue ? " · then queues the run" : ""}`;
  };

  return (
    <div className="flex flex-col gap-5">
      <Panel
        title="Process a day"
        about="Download fetches the day from the store's NVR into each camera's folder; Queue run counts the footage on disk. Runs ADD to the day's existing counts — re-running the same footage double-counts, so use “Reset day stats” first for a clean recount."
      >
        <WorkerRuntimePanel model={model} faces={faces} />
        <div className="flex flex-wrap items-end gap-3.5">
          {!single && (
            <>
              <label className={ui.field}>
                Governorate
                <Select
                  value={governorate}
                  disabled={governorates.length === 0}
                  onChange={(event) => setGovernorate(event.target.value)}
                >
                  {governorates.length === 0 && (
                    <option value="">No branch has a camera configured</option>
                  )}
                  {governorates.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              </label>
              <label className={ui.field}>
                Branch
                <Select
                  wrapperClassName="min-w-[260px]"
                  value={branchId}
                  disabled={inGovernorate.length === 0}
                  onChange={(event) => setBranchId(event.target.value)}
                >
                  {inGovernorate.length === 0 && <option value="">—</option>}
                  {inGovernorate.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.area} › {branch.name}
                      {branch.active ? "" : " (paused)"}
                    </option>
                  ))}
                </Select>
              </label>
            </>
          )}
          <label className={ui.field}>
            Day
            <input
              className={ui.input}
              type="date"
              value={runDate}
              max={yesterday()}
              onChange={(event) => setRunDate(event.target.value)}
            />
          </label>
          <label
            className={ui.field}
            title="Person detector for the run. Bigger models see more people in crowded doorways but take longer. Which sizes are installed was chosen in the installer."
          >
            Detector
            <Select
              wrapperClassName="min-w-[200px]"
              value={model}
              disabled={!detectors.data && !detectors.error}
              onChange={(event) => setModel(event.target.value)}
            >
              {/* "" asks the API for today's default, which it records on
                  the run; the catalogue only decorates the choice. */}
              {!detectors.data && !detectors.error && <option value="">Loading…</option>}
              {!detectors.data && detectors.error && <option value="">default</option>}
              {detectors.data?.options.map((option) => (
                <option key={option.id} value={option.is_default ? "" : option.id}>
                  {option.label}
                  {option.is_default ? " (default)" : ""}
                </option>
              ))}
            </Select>
          </label>
          <div className="flex flex-col gap-2 pb-1">
            <Switch
              label="Demographics"
              checked={faces}
              title="Gender/age from faces (InsightFace), face crops for the Tracks viewer, and staff recognition. Off counts entries, exits and occupancy only — several times faster."
              onChange={setFaces}
            />
            <Switch
              label="Debug"
              checked={debug}
              title="Write an annotated copy of each entrance video (boxes, track ids, entry line, crossings) to the day folder's <camera>/annotated/. Slows the run."
              onChange={setDebug}
            />
          </div>
        </div>

        <div className="mt-4">
          <AsyncBlock loading={footage.loading && footage.data === null} error={footage.error}>
            {footage.data && (
              <DayCameras
                branchId={branchId}
                cameras={footage.data.cameras}
                download={dayDownload}
                onChanged={() => {
                  nvr.reload();
                  footageChanged();
                }}
              />
            )}
          </AsyncBlock>
        </div>

        {dayDownload && downloadActive(dayDownload) && (
          <div className="mt-3 flex items-center gap-3">
            <ProgressBar
              className="flex-1"
              value={dayDownload.progress}
              note={downloadNote(dayDownload)}
            />
            <button
              type="button"
              className={ui.btnQuiet}
              onClick={() => cancelDownload(dayDownload.id)}
            >
              Cancel download
            </button>
          </div>
        )}
        {dayDownload && !downloadActive(dayDownload) && dayDownload.message && (
          <p
            className={`${ui.notice} ${
              dayDownload.status === "succeeded" ? ui.noticeSuccess : ui.noticeError
            } mt-3 mb-0`}
          >
            Download {dayDownload.status === "succeeded" ? "finished" : dayDownload.status}{" "}
            {formatDateTime(dayDownload.finished_at)}: {dayDownload.message}
          </p>
        )}
        {otherActive && (
          <p className="mt-2 text-xs text-muted">
            Also downloading {otherActive.day}
            {otherActive.progress !== null && ` (${Math.round(otherActive.progress * 100)}%)`} —
            pick that day to follow it.
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={ui.btnQuiet}
            onClick={() => download(false)}
            disabled={!canDownload || busyDay}
            title="Fetch this day's recordings from the NVR into each camera's folder"
          >
            Download
          </button>
          <button
            type="button"
            className={ui.btn}
            onClick={() => download(true)}
            disabled={!canDownload || busyDay || !canQueue}
            title="Download, then queue the run once every camera is on disk"
          >
            Download &amp; queue run
          </button>
          <button
            type="button"
            className={ui.btnQuiet}
            onClick={queue}
            disabled={!canQueue}
            title="Count the footage already on disk"
          >
            Queue run
          </button>
          <button
            type="button"
            className={`${ui.btnDanger} ml-auto`}
            onClick={() => resetStats(branchId, runDate, false)}
            disabled={!canQueue}
            title="Delete this day's footfall counts, heatmap occupancy, staff match totals and track records"
          >
            Reset day stats
          </button>
        </div>
        {(downloadHint || !canQueue || detectors.error) && (
          <p className="mt-2.5 text-xs text-muted">
            {!canQueue &&
              (single
                ? "Set up this store's entrance camera first (Store → Floor plan & cameras → Set up…). "
                : "Set up a camera on a branch before queuing runs. ")}
            {downloadHint}
            {detectors.error &&
              ` Detector list unavailable (${detectors.error}) — runs use the default.`}
          </p>
        )}
        {message && <p className={`${ui.notice} ${ui.noticeSuccess} mt-3 mb-0`}>{message}</p>}
        {error && <p className={`${ui.notice} ${ui.noticeError} mt-3 mb-0`}>{error}</p>}
      </Panel>

      <WorkersPanel />

      <AutoFetchPanel onActivity={autoFetchActivity} />

      <Panel
        title="Run history"
        subtitle={pending ? "Refreshing while runs are in flight…" : undefined}
        actions={
          <button type="button" className={ui.btnQuiet} onClick={runs.reload}>
            Refresh
          </button>
        }
      >
        {historyMessage && (
          <p className={`${ui.notice} ${ui.noticeSuccess}`}>{historyMessage}</p>
        )}
        {historyError && <p className={`${ui.notice} ${ui.noticeError}`}>{historyError}</p>}
        <AsyncBlock
          loading={runs.loading && runs.data === null}
          error={runs.error}
          empty={runs.data?.length === 0}
          emptyMessage="Nothing has been processed yet."
        >
          <div className="overflow-x-auto">
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Day</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Entries</th>
                  <th>Files</th>
                  <th>Detector</th>
                  <th>Ran on</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(runs.data ?? []).map((run) => (
                  <tr key={run.id}>
                    <td
                      className="font-mono text-xs"
                      title={
                        `Queued ${formatDateTime(run.queued_at)}` +
                        (run.requested_by ? ` by ${run.requested_by}` : "") +
                        (run.finished_at ? `\nFinished ${formatDateTime(run.finished_at)}` : "")
                      }
                    >
                      {run.id}
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="font-mono text-xs">{run.run_date}</span>
                      {!single && <div className="text-xs text-muted">{run.branch_id}</div>}
                    </td>
                    <td>
                      <StatusPill status={run.status} />
                      {run.status === "running" && (
                        <ProgressBar
                          className="mt-1.5 w-44"
                          value={run.progress}
                          note={
                            [
                              run.progress != null && `${Math.round(run.progress * 100)}%`,
                              run.progress_note ?? (run.progress == null ? "starting…" : ""),
                            ]
                              .filter(Boolean)
                              .join(" · ") || null
                          }
                        />
                      )}
                      {run.error && (
                        <details className={ui.details}>
                          <summary>error</summary>
                          <pre>{run.error}</pre>
                        </details>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      {formatDateTime(run.started_at)}
                      {run.status !== "running" && run.finished_at && run.started_at && (
                        <div className="text-xs text-muted">
                          took {formatDuration(run.started_at, run.finished_at)}
                        </div>
                      )}
                    </td>
                    <td>{formatCount(run.entries_total)}</td>
                    <td>
                      {run.files_seen == null ? (
                        "—"
                      ) : run.files && run.files.length > 0 ? (
                        <details className={`${ui.details} ${ui.detailsNeutral}`}>
                          <summary>{run.files_seen}</summary>
                          <pre>{run.files.join("\n")}</pre>
                        </details>
                      ) : (
                        run.files_seen
                      )}
                    </td>
                    <td
                      className={run.yolo_model ? "" : "text-muted"}
                      title={run.yolo_model ?? "The worker's default detector"}
                    >
                      {run.yolo_model ? detectorLabel(run.yolo_model) : "default"}
                    </td>
                    <td className="text-xs">
                      {run.runtime ? (
                        <span
                          className="whitespace-nowrap"
                          title={[run.runtime.device_label, run.runtime.decoder_line]
                            .filter(Boolean)
                            .join("\n")}
                        >
                          {runtimeSummary(run.runtime)}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                      {(run.annotate || run.faces === false) && (
                        <div className="mt-1 flex gap-1">
                          {run.annotate && <span className={ui.pill}>debug</span>}
                          {run.faces === false && (
                            <span className={`${ui.pill} normal-case!`}>no demographics</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex justify-end gap-1.5">
                        {run.status === "succeeded" && (
                          <>
                            <button
                              type="button"
                              className={ui.btnQuiet}
                              disabled={reprocessingId !== null}
                              title="Queue this day again — ADDS to its existing counts. Reset stats first for a clean recount."
                              onClick={() => reprocess(run)}
                            >
                              {reprocessingId === run.id ? "Queuing…" : "Run again"}
                            </button>
                            <button
                              type="button"
                              className={ui.btnQuiet}
                              title="Delete this day's footfall counts, heatmap occupancy, staff match totals and track records"
                              onClick={() => resetStats(run.branch_id, run.run_date, true)}
                            >
                              Reset stats
                            </button>
                            {hubStore && (
                              <button
                                type="button"
                                className={ui.btnQuiet}
                                disabled={syncingId !== null}
                                title="Upload this day's counts to the hub now, instead of waiting for the next automatic sync (every few minutes)"
                                onClick={() => sendToHub(run)}
                              >
                                {syncingId === run.id ? "Sending…" : "Send to hub"}
                              </button>
                            )}
                          </>
                        )}
                        {run.status === "queued" && (
                          <button
                            type="button"
                            className={ui.btnQuiet}
                            onClick={() => cancel(run.id)}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBlock>
      </Panel>

      {branchId && (
        <NvrPanel branchId={branchId} nvr={nvr.data} onSaved={() => nvr.reload()} />
      )}
      <FootageRootPanel version={footageVersion} onChanged={footageChanged} />
    </div>
  );
}
